import React from "react";

import Stack from "@mui/material/Stack";

import EnumSelectorInput from "../../common/inputs/EnumSelectorInput";
import NumericInput from "../../common/inputs/NumericInput";
import ToggleField from "../../common/inputs/ToggleField";
import UnitField from "../../common/inputs/UnitField";
import ValueReset from "../../common/inputs/ValueReset";
import { fieldAccess } from "../../parametersProcessor/paramAccess";
import { fieldDefault } from "../../parametersProcessor/paramDefaults";
import { ParametersContext } from "../../parametersProcessor/ParametersProcessor";
import { itemSlots, itemsToggle, resolveItems } from "../../parametersProcessor/paramItems";

/**
 * TÖBB AL-MEZŐS PARAMÉTEREK KÁRTYÁJA — egyetlen kártya két forrásra.
 *
 * Kiszolgálja a NEVESÍTETT mezős paramétereket (döntött szekrény, fióksín, és a DB-ben már felvett
 * QuadrantArcCutout / Rounding / ToeKick / Drilling / DrillSeries) ÉS a DARABSZÁM-vezérelt ismétlést
 * (polcos/fiókos zóna magasság-elosztása, tömbvágás lapjai).
 *
 * A kártya SZÁNDÉKOSAN nem ismeri a forrásokat: a `paramItems.resolveItems` a descriptor alakjából
 * modellt csinál, az `itemSlots` pedig kész, renderelhető mezőkre bontja. Minden szabály — testvér-mező
 * láthatóság, rejtett mező alapértéke, darabszám, egység-zárolás, "legalább egy arányos" invariáns —
 * a modulban van, ahol a betöltési guard és a Unity-kimenet is olvassa. Így a látszó mezők, a kosár és
 * a stylescript nem csúszhat el egymástól.
 *
 * Egy új objektum-értékű típus bekötése ezért CSAK descriptor-adat: se új komponens, se switch-ág.
 */
interface IItemsCardContentProps {
    typeDescriptor: any;
    paramValue: any;
    /**
     * A paraméter GLOBÁLIS alapértéke — ugyanolyan alakú, mint az érték. A kártya MEZŐNKÉNT rak ki
     * "visszaállítás alapértelmezettre" gombot, ezért kell neki a mezőnkénti alapérték is.
     */
    storedDefault?: any;
    onUpdateParamValue: (value: any) => void;
}

const ItemsCardContent: React.FC<IItemsCardContentProps> = ({
    typeDescriptor,
    paramValue,
    storedDefault,
    onUpdateParamValue,
}) => {
    const d = typeDescriptor;
    const paramsContext = React.useContext(ParametersContext);

    const access = fieldAccess(d);
    const model = resolveItems(d);
    const toggle = itemsToggle(model, paramValue);
    const slots = itemSlots(model, paramValue, paramsContext.parameters, storedDefault);

    if (!model) return null;

    const rows = model.source === "count" ? [...slots].reverse() : slots;

    return (
        <Stack direction="column" spacing={1.5}>
            {toggle && (
                <ToggleField
                    label={toggle.label ?? ""}
                    tooltip={toggle.tooltip ?? ""}
                    checked={toggle.checked}
                    access={access}
                    onChange={(checked) => onUpdateParamValue(toggle.emit(checked))}
                />
            )}

            {!(toggle?.checked) && rows.map((slot) => (
                <ValueReset
                    key={slot.id}
                    paramDefault={access.resettable ? fieldDefault(slot.spec, slot.storedDefault) : null}
                    currentValue={slot.value}
                    onReset={(value) => onUpdateParamValue(slot.emit(String(value)))}
                >
                    {slot.leaf.kind === "bool" ? (
                        <ToggleField
                            label={slot.label}
                            tooltip={slot.tooltip ?? ""}
                            checked={slot.value === "true"}
                            access={access}
                            onChange={(checked) => onUpdateParamValue(slot.emit(String(checked)))}
                        />
                    ) : slot.leaf.kind === "enum" ? (
                        <EnumSelectorInput
                            label={slot.label}
                            value={slot.value}
                            options={slot.leaf.options ?? {}}
                            tooltip={slot.tooltip}
                            editable={access.editable}
                            readOnly={access.readOnly}
                            onValueChange={(value) => onUpdateParamValue(slot.emit(value))}
                        />
                    ) : slot.leaf.kind === "number" ? (
                        // Egység nélküli darabszám — ugyanazokkal a fallbackokkal, mint a Count kártya.
                        <NumericInput
                            label={slot.label}
                            value={parseInt(slot.value, 10) || 0}
                            min={slot.leaf.config?.min || 0}
                            max={slot.leaf.config?.max || 1000}
                            smallStep={slot.leaf.config?.smallStep || 1}
                            largeStep={slot.leaf.config?.largeStep || 5}
                            tooltip={slot.tooltip}
                            editable={access.editable}
                            readOnly={access.readOnly}
                            onValueChanged={(num) => onUpdateParamValue(slot.emit(num.toString()))}
                        />
                    ) : (
                        <UnitField
                            label={slot.label}
                            tooltip={slot.tooltip}
                            valueStr={slot.value}
                            units={slot.leaf.units}
                            bounds={slot.leaf}
                            override={slot.override}
                            unitOptions={slot.unitOptions}
                            access={access}
                            unitChange={slot.unitChange}
                            onChange={(value) => onUpdateParamValue(slot.emit(value))}
                        />
                    )}
                </ValueReset>
            ))}
        </Stack>
    );
};

export default ItemsCardContent;
